import CheckIcon from "@mui/icons-material/Check";
import HelpOutlinedIcon from "@mui/icons-material/HelpOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Container from "@mui/material/Container";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TERMS_OF_USE_LINK } from "../../api/signupApi";
import { i18_SIGNUP } from "../../i18nReferences";
import { getHelperText } from "../../yupLocalization";
import ControlledSelection from "./ControlledSelection";
import DepositNameInput from "../common/deposit-name-input/DepositNameInput";
import { getLanguageSelectionOptions } from "../../utils/languageUtils";

/**
 * Main signup form view for new user registration
 * Contains all form fields for studio and user information
 * 
 * @param {Object} props
 * @param {Object} props.form - React Hook Form instance
 * @param {Function} props.onSubmit - Callback when form is submitted
 * @param {Array} props.countrySelection - List of available countries
 * @param {Array} props.stateSelection - List of available states/regions
 * @param {Function} props.onCountryChanged - Callback when country is changed
 * @param {string} props.studioNameInUseError - Error message if studio name is already taken
 * @param {Function} props.onStudioNameBlur - Callback when the studio name field loses focus
 * @param {boolean} props.isSubmitting - True while the registration request is running
 */
const SignupFormView = ({ 
    form, 
    onSubmit, 
    countrySelection, 
    stateSelection, 
    onCountryChanged,
    studioNameInUseError,
    onStudioNameBlur,
    isSubmitting
}) => {
    const { t, i18n } = useTranslation([i18_SIGNUP]);
    const [infoAnchorEl, setInfoAnchorEl] = useState(null);
    const [businessFieldsInfoAnchorEl, setBusinessFieldsInfoAnchorEl] = useState(null);
    const allLanguageSelections = useMemo(
        () => getLanguageSelectionOptions(i18n.language),
        [i18n.language]
    );

    // Watch studioLocale to filter available additional locales
    const studioLocale = useWatch({ control: form.control, name: "studioLocale" });
    const additionalLocales = useWatch({ control: form.control, name: "additionalLocales" });

    useEffect(() => {
        if (!studioLocale || !Array.isArray(additionalLocales)) {
            return;
        }

        if (additionalLocales.includes(studioLocale)) {
            const filteredLocales = additionalLocales.filter((locale) => locale !== studioLocale);
            form.setValue("additionalLocales", filteredLocales, {
                shouldDirty: true,
                shouldValidate: true,
            });
        }
    }, [additionalLocales, form, studioLocale]);

    // Filter out the studioLocale from available additional locales
    const availableAdditionalLocales = useMemo(() => {
        return allLanguageSelections.filter(
            (locale) => locale.id !== studioLocale
        );
    }, [allLanguageSelections, studioLocale]);

    const handleInfoClick = (event) => {
        setInfoAnchorEl(event.currentTarget);
    };

    const handleInfoClose = () => {
        setInfoAnchorEl(null);
    };

    const handleBusinessFieldsInfoClick = (event) => {
        setBusinessFieldsInfoAnchorEl(event.currentTarget);
    };

    const handleBusinessFieldsInfoClose = () => {
        setBusinessFieldsInfoAnchorEl(null);
    };

    const infoOpen = Boolean(infoAnchorEl);
    const businessFieldsInfoOpen = Boolean(businessFieldsInfoAnchorEl);

    return (
        (<form onSubmit={form.handleSubmit(onSubmit)}>
            <Container maxWidth="md">
                <Box>
                    <Typography variant="h4" sx={{
                        mt: 1
                    }}>
                        {t("MAIN.HEADER")}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontSize: "80%" }}>
                            {t("MAIN.SUBTITLE")}
                        </Typography>
                        
                        {/* Desktop/Tablet: Show dash and link text */}
                        <Box sx={{ display: { xs: "none", sm: "block" } }}>
                            <Typography variant="subtitle1" sx={{ fontSize: "80%" }}>
                                — <Link 
                                    href="https://kisscal.tattoo/kisscal-studio-manager/#preis" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                                >
                                    {t("MAIN.PRICE_CALCULATOR")}
                                    <OpenInNewIcon sx={{ fontSize: "0.9em" }} />
                                </Link>
                            </Typography>
                        </Box>
                        
                        {/* Mobile: Show only icon button */}
                        <IconButton
                            href="https://kisscal.tattoo/kisscal-studio-manager/#preis"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ 
                                display: { xs: "inline-flex", sm: "none" },
                                p: 0.5,
                                "&:hover": {
                                    backgroundColor: "rgba(0, 0, 0, 0.04)"
                                }
                            }}
                        >
                            <OpenInNewIcon sx={{ fontSize: "0.8em" }} />
                        </IconButton>
                    </Box>
                </Box>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            required
                            {...form.register("studioName", { onBlur: onStudioNameBlur })}
                            fullWidth
                            error={form.formState.errors?.studioName != null || studioNameInUseError?.length > 0}
                            helperText={getHelperText(t, form.formState.errors, "studioName") || studioNameInUseError || t(("MAIN.LOGIN_NAME_INFO_TEXT"))}
                            variant="standard"
                            label={t("MAIN.STUDIO_NAME_LABEL")}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            required
                            {...form.register("billingStudioName")}
                            fullWidth
                            error={form.formState.errors?.billingStudioName != null}
                            helperText={getHelperText(t, form.formState.errors, "billingStudioName")}
                            variant="standard"
                            label={t("MAIN.BILLING_STUDIO_NAME_LABEL")}
                        />
                    </Grid>
                    <Grid size={{ xs: 8, sm: 4, md: 5 }}>
                        <TextField
                            required
                            {...form.register("street")}
                            fullWidth
                            error={!!form.formState.errors?.street}
                            helperText={getHelperText(t, form.formState.errors, "street")}
                            variant="standard"
                            label={t("MAIN.STREET_LABEL")}
                        />
                    </Grid>
                    <Grid size={{ xs: 4, sm: 2, md: 1 }}>
                        <TextField
                            required
                            {...form.register("houseNo")}
                            fullWidth
                            error={!!form.formState.errors?.houseNo}
                            helperText={getHelperText(t, form.formState.errors, "houseNo")}
                            variant="standard"
                            label={t("MAIN.HOUSE_NO_LABEL")}
                        />
                    </Grid>
                    <Grid size={{ xs: 4, sm: 2, md: 2 }}>
                        <TextField
                            required
                            {...form.register("zip")}
                            fullWidth
                            error={!!form.formState.errors?.zip}
                            helperText={getHelperText(t, form.formState.errors, "zip")}
                            variant="standard"
                            label={t("MAIN.ZIP_LABEL")}
                        />
                    </Grid>
                    <Grid size={{ xs: 8, sm:4, md: 4 }}>
                        <TextField
                            required
                            {...form.register("city")}
                            fullWidth
                            error={!!form.formState.errors?.city}
                            helperText={getHelperText(t, form.formState.errors, "city")}
                            variant="standard"
                            label={t("MAIN.CITY_LABEL")}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <ControlledSelection
                            selections={countrySelection}
                            control={form.control}
                            required
                            fullWidth
                            onChange={onCountryChanged}
                            labelId="country-label"
                            controllerName="countryCode"
                            label={t("MAIN.COUNTRY_LABEL")}
                            data-testid="signup-country"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <ControlledSelection
                            selections={stateSelection}
                            control={form.control}
                            canEdit={stateSelection?.length > 0}
                            required
                            fullWidth
                            labelId="state-label"
                            error={!!form.formState.errors?.subregionId}
                            helperText={getHelperText(t, form.formState.errors, "subregionId")}
                            controllerName="subregionId"
                            label={t("MAIN.STATE_LABEL")}
                            data-testid="signup-state"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <ControlledSelection
                            selections={allLanguageSelections}
                            control={form.control}
                            required
                            fullWidth
                            labelId="studio-locale-label"
                            controllerName="studioLocale"
                            label={t("MAIN.STUDIO_LOCALE_LABEL")}
                            data-testid="signup-studio-locale"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Controller
                            name="additionalLocales"
                            control={form.control}
                            defaultValue={[]}
                            render={({ field, fieldState }) => (
                                <FormControl variant="standard" fullWidth error={Boolean(fieldState.error)}>
                                    <InputLabel id="additional-locales-label">
                                        {t("MAIN.ADDITIONAL_LOCALES_LABEL")}
                                    </InputLabel>
                                    <Select
                                        {...field}
                                        multiple
                                        labelId="additional-locales-label"
                                        data-testid="signup-additional-locales"
                                        renderValue={(selected) => {
                                            if (selected.length === 0) {
                                                return <em>-</em>;
                                            }
                                            return selected
                                                .map((id) => {
                                                    const locale = allLanguageSelections.find(
                                                        (l) => l.id === id
                                                    );
                                                    return locale?.text;
                                                })
                                                .join(", ");
                                        }}
                                        endAdornment={
                                            <InputAdornment position="end" sx={{ mr: 3 }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={handleInfoClick}
                                                    edge="end"
                                                >
                                                    <HelpOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                    >
                                        {availableAdditionalLocales.map((locale) => (
                                            <MenuItem key={locale.id} value={locale.id}>
                                                {locale.text}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {(() => {
                                        const message = fieldState.error?.message;
                                        if (!message) {
                                            return null;
                                        }

                                        if (typeof message === "string") {
                                            return <FormHelperText>{t(message)}</FormHelperText>;
                                        }

                                        return (
                                            <FormHelperText>
                                                {t(message.key, message.values)}
                                            </FormHelperText>
                                        );
                                    })()}
                                </FormControl>
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <DepositNameInput
                            register={form.register}
                            errors={form.formState.errors}
                            fieldName="depositName"
                            required
                        />
                    </Grid>
                    
                    {/* Business Fields Section */}
                    <Grid size={12} sx={{ mt: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Typography 
                                variant="subtitle2" 
                                color={form.formState.errors?.tattooAppointmentTypeEnabled ? "error" : "text.secondary"}
                            >
                                {t("MAIN.BUSINESS_FIELDS_HEADER")}
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={handleBusinessFieldsInfoClick}
                                sx={{ p: 0.5 }}
                            >
                                <HelpOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Controller
                                name="tattooAppointmentTypeEnabled"
                                control={form.control}
                                defaultValue={false}
                                render={({ field }) => (
                                    <Checkbox
                                        {...field}
                                        checked={field.value}
                                        onChange={(e) => {
                                            field.onChange(e.target.checked);
                                            form.trigger("tattooAppointmentTypeEnabled");
                                        }}
                                    />
                                )}
                            />
                            <Typography variant="body2">
                                {t("MAIN.TATTOO_APPOINTMENT_TYPE_LABEL")}
                            </Typography>
                        </Box>
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Controller
                                name="piercingAppointmentTypeEnabled"
                                control={form.control}
                                defaultValue={false}
                                render={({ field }) => (
                                    <Checkbox
                                        {...field}
                                        checked={field.value}
                                        onChange={(e) => {
                                            field.onChange(e.target.checked);
                                            form.trigger("tattooAppointmentTypeEnabled");
                                        }}
                                    />
                                )}
                            />
                            <Typography variant="body2">
                                {t("MAIN.PIERCING_APPOINTMENT_TYPE_LABEL")}
                            </Typography>
                        </Box>
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            required
                            {...form.register("phone")}
                            fullWidth
                            error={!!form.formState.errors?.phone}
                            helperText={getHelperText(t, form.formState.errors, "phone")}
                            variant="standard"
                            label={t("MAIN.PHONE_LABEL")}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            required
                            {...form.register("kuerzel")}
                            fullWidth
                            error={!!form.formState.errors?.kuerzel}
                            helperText={t("MAIN.ARTIST_SHORT_NAME_HELPER_TEXT")}
                            variant="standard"
                            label={t("MAIN.ARTIST_SHORT_NAME_LABEL")}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            required
                            {...form.register("email")}
                            fullWidth
                            error={!!form.formState.errors?.email}
                            variant="standard"
                            label={t("MAIN.EMAIL_LABEL")}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            required
                            {...form.register("emailConfirmation")}
                            fullWidth
                            error={!!form.formState.errors?.emailConfirmation}
                            variant="standard"
                            slotProps={{
                                input: {
                                    autoComplete: "new-password",
                                },
                            }}
                            label={t("MAIN.EMAIL_CONFIRMATION_LABEL")}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Controller
                            name="termsAgreed"
                            control={form.control}
                            defaultValue={false}
                            render={({ field }) => (
                                <Checkbox
                                    {...field}
                                    sx={{
                                        color: form.formState.errors?.termsAgreed ? "error.main" : "default",
                                        marginRight: 0,
                                    }}
                                />
                            )}
                        />
                        <Typography variant="p">
                            {t("I_ACCEPT")}
                            <Link href={TERMS_OF_USE_LINK} target="_blank">
                                {t("TERMS_OF_SERVICE")}
                            </Link>
                        </Typography>
                    </Grid>
                    <Grid size={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <IconButton
                            size="large"
                            color="success"
                            type="submit"
                            disabled={studioNameInUseError?.length > 0 || isSubmitting}
                            data-testid="signup-submit"
                        >
                            <CheckIcon />
                        </IconButton>
                    </Grid>
                </Grid>
                <Popover
                    open={infoOpen}
                    anchorEl={infoAnchorEl}
                    onClose={handleInfoClose}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                    }}
                >
                    <Box sx={{ p: 2, maxWidth: 300 }}>
                        <Typography variant="body2">
                            {t("MAIN.ADDITIONAL_LOCALES_INFO")}
                        </Typography>
                    </Box>
                </Popover>
                <Popover
                    open={businessFieldsInfoOpen}
                    anchorEl={businessFieldsInfoAnchorEl}
                    onClose={handleBusinessFieldsInfoClose}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                    }}
                >
                    <Box sx={{ p: 2, maxWidth: 400 }}>
                        <Typography variant="body2">
                            {t("MAIN.BUSINESS_FIELDS_INFO")}
                        </Typography>
                    </Box>
                </Popover>
            </Container>
        </form>)
    );
};

export default SignupFormView;

